"use client";

import type { FC, Ref } from "react";
import {
  Heading,
  type ContextAction,
  type HeadingProps,
  Chip,
  Spacing,
} from "@huub-web/ui";

import type { TemplateCollection } from "../../types";
import { TemplatesParser } from "../../components";

export interface HeadingTemplateProps {
  /**
   * The value of the heading.
   */
  value: string;
  /**
   * The description of the heading.
   * This will be rendered as a markdown string.
   */
  description?: string;
  /**
   * The variant of the heading.
   * This will determine the alignment of the heading and description.
   */
  variant?: "main" | "secondary" | "default" | "card" | "centered";
  /**
   * The subtitle of the heading.
   * This will be rendered as a text string.
   */
  subtitle?: string;
  /**
   * The trailing content of the heading.
   */
  trailingTemplates?: TemplateCollection;
  /**
   * Whether to render a ruler between the title and description.
   */
  ruler?: boolean;
  /**
   * The step of the heading.
   */
  step?: string;
  /**
   * Whether to render a centered heading.
   */
  align?: HeadingProps["align"];
  /**
   * The context actions of the heading.
   */
  contextActions?: ContextAction[];
}

export const HeadingTemplate: FC<
  HeadingTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({
  value,
  description,
  variant: variantProp = "main",
  subtitle,
  trailingTemplates,
  ruler = false,
  step,
  align: alignProp,
  contextActions,
  ref,
}) => {
  const variant = variantProp === "centered" ? "main" : variantProp;

  const align = (() => {
    if (
      (variant === "secondary" || variant === "default") &&
      trailingTemplates &&
      trailingTemplates.length > 0
    ) {
      return "left";
    }
    if (alignProp) {
      return alignProp;
    }
    return "center";
  })();

  return (
    <Heading
      ref={ref}
      title={value}
      description={description}
      variant={variant}
      align={align}
      subtitle={subtitle}
      leading={
        <>
          <Spacing />
          {step && (
            <Chip variant="tertiary" size="l" style={{ alignSelf: "center" }}>
              {step}
            </Chip>
          )}
        </>
      }
      trailing={
        trailingTemplates &&
        trailingTemplates.length > 0 && (
          <TemplatesParser templates={trailingTemplates} level={1} />
        )
      }
      ruler={ruler}
      contextActions={contextActions}
    />
  );
};
